import React from 'react';
import styled from 'styled-components';

import { CARD_TYPES } from '../../../shared/constants';
import { IContentfulLink, IImage, IParentPage } from '../../../types/shared';

import { ChevronRightLarge } from '../../atoms/icons/ChevronRightLarge';
import Link from '../../atoms/link/Link';

import { DEQ_TYPES } from '../../../utils/deq/deq.constants';
import { getInternalLink } from '../../../utils/generic/linkGenerator';
import { parseSectionID } from '../../../utils/string-utils';

import mq from '../../../styles/breakpoints';
import Colors from '../../../styles/colors';
import { H3 } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

interface TipProps {
  title: string;
  primaryLink: IContentfulLink;
  image?: IImage;
  anchorName?: string;
}

const Tip = ({ title, image, primaryLink, anchorName }: TipProps) => {
  const link = primaryLink?.linkedPage
    ? `/${getInternalLink(primaryLink?.linkedPage as IParentPage)}`
    : (primaryLink?.externalUrl as string);

  const id = parseSectionID(CARD_TYPES.tip, anchorName);

  return (
    <StyledLink
      href={link}
      data-testid="tip"
      trackingComponent="tip"
      trackingType={DEQ_TYPES.btnCard}
      trackingLabel={`${title}`.toLowerCase()}
      id={id}
    >
      {image && (
        <ImageContainer>
          <img
            data-testid="image"
            alt={image?.description || 'tip-icon'}
            src={image.file?.url || image}
          />
        </ImageContainer>
      )}
      <InnerContainer>
        <StyledH3 data-testid="title">{title}</StyledH3>
        <StyledIconContainer>
          <ChevronRightLarge color={Colors.white} />
        </StyledIconContainer>
      </InnerContainer>
    </StyledLink>
  );
};

export default Tip;

const StyledIconContainer = styled.div`
  cursor: pointer;
  height: 24px;
  margin-left: ${Spacings.S};
  transition: transform 0.1s ease-in-out;
  width: ${Spacings.M};

  ${mq.from.breakpoint.M`
    margin-left: 2px;
  `}
`;

const StyledLink = styled(Link)`
  align-items: center;
  background: ${Colors.abnGradientG400toG200};
  border-radius: ${Spacings.XXS};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  padding: ${Spacings.S} ${Spacings.S} ${Spacings.L};
  row-gap: ${Spacings.XS};
  width: 100%;

  ${mq.from.breakpoint.M`
    row-gap: initial;
    flex-direction: row;
    padding: ${Spacings.M};
  `}

  ${mq.from.breakpoint.L`
    &:hover {

    ${StyledIconContainer} {
        transform: translateX(4px);
        transition: transform 0.1s ease-in-out;
    }

    &:active {
      box-shadow: none;
    }
 `}
`;

const ImageContainer = styled.div`
  height: 80px;
  position: relative;
  width: 80px;

  img {
    height: inherit;
    width: inherit;
  }

  ${mq.from.breakpoint.M`
    position: absolute;
    height: 148px;
    width: 148px;
  `}

  ${mq.from.breakpoint.L`
    height: 192px;
    width: 192px;
  `}
`;

const StyledH3 = styled(H3)`
  color: ${Colors.white};
  margin: 0;
  text-align: center;
  text-decoration: none;

  ${mq.from.breakpoint.M`
    text-align: left;
  `}
`;

const InnerContainer = styled.div`
  align-items: center;
  display: inline-flex;
  padding-left: 0;

  ${mq.from.breakpoint.M`
    max-width: 423px;
    margin: 0 auto 0 148px;
    padding-left: ${Spacings.M};
  `}

  ${mq.from.breakpoint.L`
    max-width: 1008px;
    margin: 0 auto 0 192px;
  `}
`;
